// Rolling inactivity window for the signed-in session.
//
// Firebase refresh tokens don't expire on their own, so without an explicit
// policy a session lasts forever. This module adds a 1-week *inactivity*
// timeout: as long as the user opens or interacts with the app at least once
// every 7 days the session stays alive; after a full week of no activity the
// next load (or an open tab) signs them out.
//
// The "last activity" timestamp is kept in localStorage so it's shared across
// tabs and survives reloads. This module is intentionally free of any Firebase
// imports — the consumer passes in a callback to run when the session expires.

const STORAGE_KEY = "collavue:lastActivityAt";

// 1 week of inactivity before we sign the user out.
export const INACTIVITY_LIMIT_MS = 7 * 24 * 60 * 60 * 1000;

// How often to re-check for expiry while a tab stays open.
const CHECK_INTERVAL_MS = 60 * 1000;

// Throttle localStorage writes from high-frequency events (mousemove/scroll).
const ACTIVITY_THROTTLE_MS = 30 * 1000;

// Interaction events that count as the user being active.
const ACTIVITY_EVENTS = [
  "click",
  "keydown",
  "mousedown",
  "touchstart",
  "scroll",
  "mousemove",
];

let intervalId = null;
let lastWrite = 0;
let started = false;
let onExpire = null;

const now = () => Date.now();

export function recordActivity(force = false) {
  const t = now();
  if (!force && t - lastWrite < ACTIVITY_THROTTLE_MS) return;
  lastWrite = t;
  try {
    window.localStorage.setItem(STORAGE_KEY, String(t));
  } catch {
    /* storage may be unavailable (private mode, quota) — ignore */
  }
}

export function getLastActivity() {
  try {
    const v = Number(window.localStorage.getItem(STORAGE_KEY));
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}

export function clearActivity() {
  lastWrite = 0;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

// Expired only when we have a recorded timestamp older than the limit. With no
// record (e.g. a brand-new sign-in or first load after deploy) we treat the
// session as valid so nobody gets locked out unexpectedly.
export function isSessionExpired() {
  const last = getLastActivity();
  if (last == null) return false;
  return now() - last > INACTIVITY_LIMIT_MS;
}

function handleActivity() {
  // Background tabs shouldn't keep a session alive on a timer.
  if (typeof document !== "undefined" && document.visibilityState === "hidden") {
    return;
  }
  recordActivity();
}

function checkExpiry() {
  if (isSessionExpired()) {
    stopActivityTracking();
    if (typeof onExpire === "function") onExpire();
  }
}

// Begin tracking interaction and watching for expiry. Safe to call repeatedly;
// subsequent calls just refresh the activity stamp (and the expiry callback).
export function startActivityTracking(onExpireCb) {
  onExpire = onExpireCb || onExpire;
  if (started) {
    recordActivity(true);
    return;
  }
  if (typeof window === "undefined") return;
  started = true;
  recordActivity(true);

  ACTIVITY_EVENTS.forEach((evt) =>
    window.addEventListener(evt, handleActivity, { passive: true })
  );
  // Returning to a backgrounded tab should re-check the window immediately.
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", checkExpiry);
  }
  window.addEventListener("focus", checkExpiry);
  intervalId = window.setInterval(checkExpiry, CHECK_INTERVAL_MS);
}

export function stopActivityTracking() {
  if (!started) return;
  started = false;
  if (typeof window !== "undefined") {
    ACTIVITY_EVENTS.forEach((evt) =>
      window.removeEventListener(evt, handleActivity)
    );
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", checkExpiry);
    }
    window.removeEventListener("focus", checkExpiry);
  }
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
}
